import React, { useState } from 'react';
import { useSetCurrentPage, useAuthUser, useUser } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import TelegramAvatar from '../components/ui/TelegramAvatar';
import PageHeader from '../components/PageHeader';
import { adminService } from '../services/adminService';

const PersonalCenter: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const authUser = useAuthUser();
  const telegramUser = useUser();
  const showToast = useToastStore((state) => state.show);
  const [heapDumpLoading, setHeapDumpLoading] = useState(false);

  // 常用功能 - 我的关注、我的自选、我的监控、我的下单和我的委托
  const commonFunctions = [
    {
      icon: '👁️',
      label: '我的关注',
      action: () => setCurrentPage('watchlist'),
    },
    {
      icon: '⭐',
      label: '我的自选',
      action: () => {
        sessionStorage.setItem('watchlist_self_refresh_flag', 'true');
        setCurrentPage('watchlist-self');
      },
    },
    {
      icon: '🔔',
      label: '我的监控',
      action: () => setCurrentPage('my-monitors'),
    },
    {
      icon: '📋',
      label: '我的下单',
      action: () => setCurrentPage('my-orders'),
    },
    {
      icon: '📝',
      label: '我的委托',
      action: () => setCurrentPage('my-pending-orders'),
    },
    {
      icon: '🤖',
      label: '策略广场',
      action: () => setCurrentPage('strategy-square'),
    },
    {
      icon: '📊',
      label: '信号记录',
      action: () => setCurrentPage('signal-triggers'),
    },
    {
      icon: '🔍',
      label: '信号嗅探',
      action: () => setCurrentPage('signal-scanner'),
    },
  ];

  // 生成内存快照
  const handleGenerateHeapDump = async () => {
    if (heapDumpLoading) return;

    try {
      setHeapDumpLoading(true);
      const result = await adminService.generateHeapDump();

      if (result.success && result.data) {
        showToast(
          `内存快照已生成\n文件: ${result.data.filename}\n堆内存: ${result.data.memoryUsage.heapUsed}MB / ${result.data.memoryUsage.heapTotal}MB`,
          'success',
        );
      } else {
        showToast(result.message || '生成内存快照失败', 'error');
      }
    } catch (error: any) {
      console.error('生成内存快照失败:', error);
      showToast(error.message || '生成内存快照失败', 'error');
    } finally {
      setHeapDumpLoading(false);
    }
  };

  // 超级管理模块（只有超级管理员可见）
  const isSuperAdmin = authUser?.role === 'SUPER_ADMIN';
  const superAdminFunctions = isSuperAdmin
    ? [
        {
          icon: '👥',
          label: '用户列表',
          action: () => setCurrentPage('user-list'),
        },
        {
          icon: '⚙️',
          label: '系统任务',
          action: () => setCurrentPage('system-tasks'),
        },
        {
          icon: '📝',
          label: '提示词管理',
          action: () => setCurrentPage('system-prompts'),
        },
        {
          icon: '🤖',
          label: '大模型管理',
          action: () => setCurrentPage('ai-model-management'),
        },
        {
          icon: '📊',
          label: '交易记录',
          action: () => setCurrentPage('auto-trading-records'),
        },
        {
          icon: '📈',
          label: '历史持仓',
          action: () => setCurrentPage('historical-positions'),
        },
        {
          icon: '💾',
          label: '内存快照',
          action: handleGenerateHeapDump,
          loading: heapDumpLoading,
        },
      ]
    : [];

  // 资产管理 - 钱包
  const assetManagement = [{ icon: '👛', label: '我的钱包', action: () => setCurrentPage('wallet') }];

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader title="个人中心" onBack={() => setCurrentPage('dashboard')} />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {/* 用户信息区域 */}
          <div className="pt-6 pb-4">
            <div className="flex items-center gap-4">
              <TelegramAvatar
                photoUrl={telegramUser?.photo_url || authUser?.photoUrl || undefined}
                firstName={telegramUser?.first_name || authUser?.firstName || ''}
                lastName={telegramUser?.last_name || authUser?.lastName || ''}
                username={telegramUser?.username || authUser?.username || undefined}
                isPremium={telegramUser?.is_premium || authUser?.isPremium || false}
                size="lg"
                animated={false}
                showStatus={false}
                showPremium={false}
              />
              <div className="flex-1">
                <div className="text-xl font-bold text-white mb-1">
                  {(telegramUser?.first_name || authUser?.firstName || '') +
                    ' ' +
                    (telegramUser?.last_name || authUser?.lastName || '')}
                </div>
                {(telegramUser?.username || authUser?.username) && (
                  <div className="text-sm text-white/60 flex items-center gap-2">
                    @{telegramUser?.username || authUser?.username}
                    {authUser?.role && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white/80 border border-white/20">
                        {authUser.role}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 分割线 */}
          <div className="flex items-center gap-2 my-6">
            <h2 className="text-white/90 text-lg font-bold">常用功能</h2>
            <div className="flex-1 h-px bg-gradient-to-r from-white/20 to-transparent"></div>
          </div>

          {/* 常用功能 */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-4">
              {commonFunctions.map((func, index) => (
                <button
                  key={index}
                  onClick={func.action}
                  className="flex flex-col items-center gap-2 p-4 bg-white/5 border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
                >
                  <span className="text-2xl">{func.icon}</span>
                  <span className="text-sm text-white/90 font-medium">{func.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 超级管理模块（只有超级管理员可见） */}
          {isSuperAdmin && superAdminFunctions.length > 0 && (
            <>
              {/* 分割线 */}
              <div className="flex items-center gap-2 my-6">
                <h2 className="text-white/90 text-lg font-bold">超级管理</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-white/20 to-transparent"></div>
              </div>

              <div className="mb-6">
                <div className="grid grid-cols-2 gap-4">
                  {superAdminFunctions.map((func, index) => (
                    <button
                      key={index}
                      onClick={func.action}
                      disabled={(func as any).loading}
                      className={`flex flex-col items-center gap-2 p-4 bg-white/5 border border-white/20 rounded-xl hover:bg-white/10 transition-colors ${
                        (func as any).loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {(func as any).loading ? (
                        <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      ) : (
                        <span className="text-2xl">{func.icon}</span>
                      )}
                      <span className="text-sm text-white/90 font-medium">{func.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* 分割线 */}
          <div className="flex items-center gap-2 my-6">
            <h2 className="text-white/90 text-lg font-bold">资产管理</h2>
            <div className="flex-1 h-px bg-gradient-to-r from-white/20 to-transparent"></div>
          </div>

          {/* 资产管理 */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-4">
              {assetManagement.map((item, index) => (
                <button
                  key={index}
                  onClick={item.action}
                  className="flex flex-col items-center gap-2 p-4 bg-white/5 border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
                >
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm text-white/90 font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </GradientBackground>
  );
};

export default PersonalCenter;
